import { css } from 'lit';

/**
 * Design tokens — Embrace Design System V2 (zie docs/design.md §10).
 * Default-palet is geijkt op het Embrace-portfolio voor woningcorporaties;
 * klanten met een eigen huisstijl overschrijven de tokens via
 * `embrace-chat { --ec-primary: ...; }` in de host-CSS.
 */
export const tokens = css`
  :host {
    /* ============================================================
     * TIER 1 — ESSENTIALS (klant-overschrijfbaar)
     * ============================================================ */
    --ec-primary: #0C1C47;         /* Embrace navy (default) */
    --ec-on-primary: #FFFFFF;
    --ec-accent: #20AAA2;          /* Embrace teal */
    --ec-text: #21335C;            /* Primary ink */
    --ec-background: #FFFFFF;
    --ec-font-family: 'Figtree', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --ec-font-display: 'Figtree', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

    /* ============================================================
     * TIER 2 — DERIVED
     * ============================================================ */
    --ec-primary-hover:    color-mix(in oklch, var(--ec-primary) 88%, #fff);
    --ec-primary-active:   color-mix(in oklch, var(--ec-primary) 78%, #000);
    --ec-primary-disabled: #C8CCD4;
    --ec-focus-ring: color-mix(in srgb, var(--ec-primary) 30%, transparent);
    --ec-focus-ring-width: 3px;
    --ec-focus-ring-offset: 2px;

    --ec-text-muted: #5E5F62;
    --ec-text-soft: #8C8D8F;
    --ec-timestamp-color: #8C8D8F;

    /* Header-gradient — bredere navy tinten */
    --ec-header-gradient: linear-gradient(160deg, #0C1C47 0%, #22356A 45%, #0C1C47 100%);
    --ec-header-bg: var(--ec-header-gradient);
    --ec-launcher-bg: var(--ec-primary);
    --ec-panel-bg: var(--ec-background);

    /* Message bubbles — bezoeker (eigen berichten) */
    --ec-my-message-bg: #0C57E1;   /* Primary action blauw uit design */
    --ec-on-my-message: #FFFFFF;
    --ec-my-message-border-width: 0;
    --ec-my-message-border-style: solid;
    --ec-my-message-border-color: transparent;

    /* Message bubbles — medewerker / operator */
    --ec-their-message-bg: #F1F2F4;
    --ec-on-their-message: #21335C;
    --ec-their-message-border-width: 0;
    --ec-their-message-border-style: solid;
    --ec-their-message-border-color: transparent;

    /* Virtuele assistent (bot) — paars-avatar voor onderscheid van mens-
       medewerker. De bubble-achtergrond is bewust los van de operator-
       bubble definieerbaar zodat klanten een eigen bot-accent kunnen
       zetten zonder de operator-look mee te slepen. Default matched
       hier visueel met de operator-bubble, maar als losse waarde
       (geen var-ref) zodat overschrijven van operator-bg niet stiekem
       de bot-bubble meeverandert. */
    --ec-assistant-bg: #7C3AED;       /* avatar-cirkel */
    --ec-on-assistant: #FFFFFF;       /* avatar-tekst */
    --ec-assistant-message-bg: #F1F2F4;
    --ec-on-assistant-message: #21335C;
    --ec-assistant-message-border-width: 0;
    --ec-assistant-message-border-style: solid;
    --ec-assistant-message-border-color: transparent;

    /* Primary gradient (convenience) */
    --ec-primary-gradient: var(--ec-header-gradient);

    /* Form elements */
    --ec-input-bg: #FFFFFF;
    --ec-input-border: rgba(0,0,0,0.08);
    --ec-input-border-hover: rgba(12,28,71,0.35);
    --ec-input-border-focus: var(--ec-primary);
    --ec-input-placeholder: #8C8D8F;

    /* ============================================================
     * TIER 3 — STRUCTURAL
     * ============================================================ */
    --ec-error: #CB1429;
    --ec-on-error: #FFFFFF;
    --ec-error-subtle: #FEF2F3;
    --ec-success: #2D8449;
    --ec-on-success: #FFFFFF;
    --ec-warning: #AB650F;
    --ec-warning-subtle: #FEF6EB;

    --ec-border: rgba(0,0,0,0.08);
    --ec-border-strong: rgba(0,0,0,0.18);
    --ec-surface-sunken: #F6F7F9;

    /* Shadows — uit design-system */
    --ec-shadow-panel: 0 40px 100px -20px rgba(12,28,71,.35), 0 8px 24px rgba(0,0,0,.08);
    --ec-shadow-launcher: 0 16px 40px rgba(12,28,71,.35), 0 2px 6px rgba(0,0,0,.12);
    --ec-shadow-sm: 0 1px 2px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.04);
    --ec-shadow-md: 0 4px 12px rgba(0,0,0,0.08);

    /* Radius */
    --ec-radius-panel: 20px;
    --ec-radius-bubble: 18px;
    --ec-radius-bubble-tail: 6px;
    --ec-radius-button: 10px;
    --ec-radius-input: 14px;
    --ec-radius-card: 16px;

    /* Sizing */
    --ec-launcher-size: 60px;
    --ec-panel-width: 400px;
    --ec-panel-max-height: 640px;

    /* Position */
    --ec-alignment: right;
    --ec-offset-x: 24px;
    --ec-offset-y: 24px;
    --ec-z-index: 1000;

    /* ============================================================
     * MOTION
     * ============================================================ */
    --ec-duration-instant: 0ms;
    --ec-duration-fast: 150ms;
    --ec-duration-base: 220ms;
    --ec-duration-slow: 400ms;

    --ec-ease-enter: cubic-bezier(0.2, 0, 0, 1);
    --ec-ease-exit: cubic-bezier(0.4, 0, 1, 1);
    --ec-ease-smooth: cubic-bezier(0.4, 0, 0.2, 1);
    --ec-ease-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);

    --ec-panel-open-duration: var(--ec-duration-base);
    --ec-panel-close-duration: var(--ec-duration-fast);
    --ec-launcher-hover-duration: var(--ec-duration-fast);
    --ec-message-appear-duration: var(--ec-duration-slow);
    --ec-typing-indicator-duration: 1200ms;

    --ec-transition-launcher-hover:
      transform var(--ec-launcher-hover-duration) var(--ec-ease-smooth),
      background var(--ec-launcher-hover-duration) var(--ec-ease-smooth);
    --ec-transition-panel-open:
      opacity var(--ec-panel-open-duration) var(--ec-ease-enter),
      transform var(--ec-panel-open-duration) var(--ec-ease-enter);
    --ec-transition-panel-close:
      opacity var(--ec-panel-close-duration) var(--ec-ease-exit),
      transform var(--ec-panel-close-duration) var(--ec-ease-exit);
    --ec-transition-message:
      opacity var(--ec-message-appear-duration) var(--ec-ease-enter),
      transform var(--ec-message-appear-duration) var(--ec-ease-enter);

    font-family: var(--ec-font-family);
    color: var(--ec-text);
  }

  @media (prefers-reduced-motion: reduce) {
    :host {
      --ec-duration-instant: 0ms;
      --ec-duration-fast: 0ms;
      --ec-duration-base: 0ms;
      --ec-duration-slow: 0ms;
      --ec-typing-indicator-duration: 0ms;
    }
  }
`;

/**
 * Figtree-font eenmalig laden in het light-DOM (via Google Fonts).
 * Shadow DOM kan @font-face niet zelf toepassen voor geïnjecteerde stylesheets;
 * de declaratie moet in het hoofd-document staan en cascadet dan door.
 *
 * Idempotent — veilig om meerdere keren aan te roepen.
 */
const FONT_LINK_ID = 'embrace-chat-figtree-font';

export function ensureFigtreeFont(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_LINK_ID)) return;

  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';

  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800&display=swap';

  document.head.appendChild(preconnect1);
  document.head.appendChild(preconnect2);
  document.head.appendChild(link);
}
